import type { Prisma } from "@gt/prisma";
import { hasCreatedCompanyProfile } from "@shared/companyVerificationGates";
import { getClusterSignupHomePath } from "@/pages/my-cluster/myCluster.const";
import { isClusterPortalRole } from "@/shared/utils/clusterPortalRole.util";
import { isExpoVisitorRole } from "@/shared/utils/expoVisitorRole.util";

export type UserWithCompany = Prisma.UserGetPayload<{ include: { company: true } }> & {
  signupSource?: "MAIN" | "CLUSTER" | "EXPO" | null;
  signupClusterSlug?: string | null;
  signupExpoSlug?: string | null;
  expoDirectoryGuest?: boolean | null;
  role?: string | null;
  signupCluster?: {
    headUserId: string;
    industry: string;
    corridorSignupFreeCollaborationEnabled?: boolean;
    clusterSignupCompanyIntentEnabled?: boolean;
  } | null;
};

type CompanyWithOptionalAdminInvite = UserWithCompany["company"] & {
  adminCreatedInvite?: { status: string } | null;
};

/** Company statuses that may browse, shortlist and complete the intent questionnaire. */
export const EXPLORE_OPPORTUNITIES_STATUSES = ["SYSTEM_VERIFIED", "UNDER_REVIEW", "VERIFIED"] as const;

export {
  canExploreOpportunities,
  canSendInterestRequest,
} from "@shared/companyVerificationGates";

/** Company has passed external/system verification — full app routes allowed. */
export const CLIENT_ONBOARDING_COMPLETE_STATUSES = new Set(["SYSTEM_VERIFIED", "VERIFIED", "UNDER_REVIEW"]);

export function isClientOnboardingComplete(user: UserWithCompany | null | undefined): boolean {
  const status = user?.company?.status;
  return Boolean(user?.company && status && CLIENT_ONBOARDING_COMPLETE_STATUSES.has(status));
}

/**
 * True once the client has saved a real company profile (not just signup shell).
 * Derived only from API user/company payload — no localStorage.
 *
 * MAIN/cluster/expo signup may store a website on the shell company while HQ is still
 * pending. Website alone must not count as profile-created, or users skip
 * `/create-profile` and jump straight to registry verification on Explore/Home.
 */
export function hasClientCreatedProfile(user: UserWithCompany | null | undefined): boolean {
  return hasCreatedCompanyProfile(user?.company);
}

/**
 * Main client signup may browse Explore and Home before the company form.
 * Cluster signup, expo signup, and admin-invite claims keep the forced form.
 */
export function shouldDeferCompanyProfile(user: UserWithCompany | null | undefined): boolean {
  if (!user || hasClientCreatedProfile(user)) return false;
  if (isExpoSignupUser(user) || isClusterSignupUser(user)) return false;
  if (user.signupSource === "EXPO" || user.signupSource === "CLUSTER") return false;
  const invite = (user.company as CompanyWithOptionalAdminInvite | null | undefined)?.adminCreatedInvite;
  if (invite) return false;
  return true;
}

/** Background website-profile job is still filling the shell company. */
export const WEBSITE_PROFILE_JOB_IN_PROGRESS_STATUSES = new Set([
  "QUEUED",
  "EXTRACTING",
  "DRAFTING",
  "CREATING",
]);

export function isWebsiteProfileJobInProgress(
  company: { websiteProfileJob?: { status?: string | null } | null } | null | undefined,
): boolean {
  const status = company?.websiteProfileJob?.status;
  return Boolean(status && WEBSITE_PROFILE_JOB_IN_PROGRESS_STATUSES.has(status));
}

/** Generated profile is waiting on /profile/edit even when headquarters is still the signup placeholder. */
export function canReviewGeneratedCompanyProfile(
  user: UserWithCompany | null | undefined,
  path: string,
): boolean {
  if (path !== "/profile/edit") return false;
  const company = user?.company as
    | { websiteProfileJob?: { status?: string | null } | null }
    | null
    | undefined;
  return company?.websiteProfileJob?.status === "READY_FOR_REVIEW";
}

/** Routes a deferred-profile user may open. Company preview is part of Explore. */
export function isDeferredProfileBrowsePath(path: string): boolean {
  if (path === "/my-discovery" || path.startsWith("/my-discovery/")) return true;
  if (path === "/home" || path === "/dashboard") return true;
  if (path === "/create-profile") return true;
  if (path === "/settings" || path === "/account" || path === "/two-factor/setup") return true;
  if (path === "/my-cluster" || path.startsWith("/my-cluster/")) return true;
  if (path === "/expos" || path.startsWith("/expos/")) return true;
  if (path === "/my-expos" || path.startsWith("/my-expos/")) return true;
  if (path === "/ecosystem-heads" || path.startsWith("/ecosystem-heads/")) return true;
  if (path === "/experts" || path.startsWith("/experts/")) return true;
  if (path === "/experts-and-corridors" || path.startsWith("/experts-and-corridors/")) return true;
  if (path === "/sector" || path.startsWith("/sector/")) return true;
  return /^\/collaborations\/company\/[^/]+$/.test(path);
}

/**
 * True when registry verification CTAs / modal should show (GST, CIN, etc.).
 * Profile completion stays DRAFT until the registration number is submitted.
 * UNDER_REVIEW means that submit already went to admin, so the CTA stays hidden.
 */
export function shouldPromptCompanyRegistryVerification(
  user: UserWithCompany | null | undefined
): boolean {
  const company = user?.company as
    | (NonNullable<UserWithCompany["company"]> & {
        websiteProfileJob?: { status?: string | null } | null;
      })
    | null
    | undefined;
  if (company?.websiteProfileJob?.status === "READY_FOR_REVIEW") return false;
  if (!company || !hasClientCreatedProfile(user)) return false;

  const status = company.status;
  if (!status || status === "VERIFIED" || status === "SYSTEM_VERIFIED") return false;

  if (status === "UNDER_REVIEW") return false;

  return true;
}

export function isFlexDemoAssigned(user: UserWithCompany | null | undefined): boolean {
  const company = user?.company;
  if (!company) return false;
  return Boolean(company.flexDemoRequired && !company.flexDemoCompletedAt);
}

/** GST / registry details have been submitted (modal no longer required). */
export function hasSubmittedRegistryVerification(user: UserWithCompany | null | undefined): boolean {
  return hasClientCreatedProfile(user) && !shouldPromptCompanyRegistryVerification(user);
}

/** Company intent 15-question questionnaire has been submitted. */
export function hasCompletedCompanyIntent(user: UserWithCompany | null | undefined): boolean {
  const completedAt = (
    user?.company as { intentQuestionnaire?: { completedAt?: Date | string | null } | null } | null | undefined
  )?.intentQuestionnaire?.completedAt;
  return Boolean(completedAt);
}

/** Hard-lock into the sandbox demo only after GST submit and the 15-question intent form. */
export function isClusterSignupUser(user: UserWithCompany | null | undefined): boolean {
  return user?.signupSource === "CLUSTER";
}

export function isExpoSignupUser(user: UserWithCompany | null | undefined): boolean {
  if (isExpoVisitorRole(user?.role)) return true;
  return user?.expoDirectoryGuest === true && Boolean(user.signupExpoSlug?.trim());
}

export function getExpoSignupSlug(user: UserWithCompany | null | undefined): string | null {
  const slug = user?.signupExpoSlug?.trim();
  return isExpoSignupUser(user) && slug ? slug : null;
}

/** Expo-visitor app home: catalog of all expos (not locked Explore). */
export const EXPO_VISITOR_HOME_PATH = "/expos";

/** True when cluster-signup origin corridor requires platform 15Q in onboarding (default ON). */
export function isClusterSignupPlatformIntentEnabled(
  user: UserWithCompany | null | undefined
): boolean {
  if (!isClusterSignupUser(user)) return false;
  return user?.signupCluster?.clusterSignupCompanyIntentEnabled !== false;
}

export function needsFlexDemo(user: UserWithCompany | null | undefined): boolean {
  if (isClusterSignupUser(user)) return false;
  return (
    isFlexDemoAssigned(user) &&
    hasSubmittedRegistryVerification(user) &&
    hasCompletedCompanyIntent(user)
  );
}

/** Home after profile exists (create-profile complete, skip the empty-profile gate). */
export function getClientHomePathAfterProfile(user: UserWithCompany | null | undefined): string {
  if (needsFlexDemo(user)) return "/flex-demo";
  const clusterHome = getClusterSignupHomePath(user);
  if (clusterHome) return clusterHome;
  return "/my-discovery";
}

/** Where to send a CLIENT after auth when using default app shell (not admin). */
export function getDefaultClientPathAfterAuth(user: UserWithCompany | null | undefined): string {
  // API failure / unknown user: never assume "new user" — avoid trapping existing clients on create-profile.
  if (!user) return "/my-discovery";
  if (isExpoSignupUser(user)) return EXPO_VISITOR_HOME_PATH;
  if (!hasClientCreatedProfile(user)) {
    if (shouldDeferCompanyProfile(user)) return "/my-discovery";
    return "/create-profile";
  }
  return getClientHomePathAfterProfile(user);
}

/** Post-login destination from portal user role (clients, facilitators, admins). */
export function getDefaultPathAfterAuth(user: UserWithCompany | null | undefined): string {
  if (!user) return "/my-discovery";
  if (user.role === "FACILITATOR") return "/facilitator/dashboard";
  if (isClusterPortalRole(user.role)) return "/ecosystem-head/dashboard";
  if (user.role === "EXPO_HEAD") return "/expo-head/dashboard";
  if (user.role === "EXPERT") return "/expert/dashboard";
  if (user.role === "MANAGER" || isExpoVisitorRole(user.role)) return getDefaultClientPathAfterAuth(user);
  if (user.role === "ADMIN" || user.role === "SUB_ADMIN") return "/admin/companies";
  return getDefaultClientPathAfterAuth(user);
}
